/**
 * Snap engine — SketchUp-style inference for the sketch tools.
 *
 * Candidates come from two sources: the model (display edges of visible
 * bodies that LIE IN the active sketch plane — face boundaries when
 * sketching on a face) and the sketch's own entities. Priority:
 *
 *   endpoint / midpoint / center / origin  (point snaps, nearest wins)
 *   > on-edge  (perpendicular foot on an edge segment)
 *   > axis     (H/V alignment with the previous point, for lines)
 *   > grid
 *
 * Everything here is pure math over UV coordinates — the sketch tool owns
 * projection, tolerance-in-pixels conversion, and the visual markers.
 */

import type { Vec2 } from '../timeline/types';

export type SnapKind =
  | 'endpoint'
  | 'midpoint'
  | 'center'
  | 'origin'
  | 'on-edge'
  | 'extension'
  | 'parallel'
  | 'perpendicular'
  | 'axis-x' // parallel to / on the sketch X axis (red, SketchUp-style)
  | 'axis-y' // parallel to / on the sketch Y axis (green)
  | 'grid';

export interface SnapPoint {
  readonly p: Vec2;
  readonly kind: SnapKind;
}

export interface SnapSegment {
  readonly a: Vec2;
  readonly b: Vec2;
}

export interface SnapResult {
  readonly p: Vec2;
  readonly kind: SnapKind;
  /** Inference ghost line to draw (extension/parallel/perpendicular/axis). */
  readonly ghost?: SnapSegment;
}

/**
 * Project body display edges into the sketch plane and keep only segments
 * that lie in it (within `planeTol`). Returns endpoint+midpoint snap points
 * (deduped) and the segments themselves for on-edge snapping.
 *
 * When `vertexPositions` (true B-rep vertices) is provided, ONLY those count
 * as 'endpoint' — polyline joints along discretized arcs are curve samples,
 * not corners — and midpoints are emitted only for straight edges (segments
 * whose both ends are true vertices).
 */
export function collectPlanarSnaps(
  edgePositions: Float32Array,
  planeToWorld: Float32Array,
  planeTol = 0.5,
  vertexPositions?: Float32Array,
): { points: SnapPoint[]; segments: SnapSegment[] } {
  const m = planeToWorld;
  const o = [m[12]!, m[13]!, m[14]!];
  const u = [m[0]!, m[1]!, m[2]!];
  const v = [m[4]!, m[5]!, m[6]!];
  const n = [m[8]!, m[9]!, m[10]!];

  const toPlane = (x: number, y: number, z: number): { uv: Vec2; dist: number } => {
    const rx = x - o[0]!;
    const ry = y - o[1]!;
    const rz = z - o[2]!;
    return {
      uv: [
        rx * u[0]! + ry * u[1]! + rz * u[2]!,
        rx * v[0]! + ry * v[1]! + rz * v[2]!,
      ],
      dist: rx * n[0]! + ry * n[1]! + rz * n[2]!,
    };
  };

  const points: SnapPoint[] = [];
  const segments: SnapSegment[] = [];
  const seen = new Set<string>();
  const addPoint = (p: Vec2, kind: SnapKind): void => {
    const key = `${kind}:${Math.round(p[0] * 1e4)}:${Math.round(p[1] * 1e4)}`;
    if (seen.has(key)) return;
    seen.add(key);
    points.push({ p, kind });
  };

  // True vertices (exact corners), when the kernel provides them.
  const hasTrueVerts = vertexPositions !== undefined && vertexPositions.length > 0;
  const trueVertKeys = new Set<string>();
  if (hasTrueVerts) {
    for (let i = 0; i + 2 < vertexPositions.length; i += 3) {
      const { uv, dist } = toPlane(
        vertexPositions[i]!,
        vertexPositions[i + 1]!,
        vertexPositions[i + 2]!,
      );
      if (Math.abs(dist) > planeTol) continue;
      addPoint(uv, 'endpoint');
      trueVertKeys.add(`${Math.round(uv[0] * 1e3)}:${Math.round(uv[1] * 1e3)}`);
    }
  }
  const isTrueVert = (p: Vec2): boolean =>
    trueVertKeys.has(`${Math.round(p[0] * 1e3)}:${Math.round(p[1] * 1e3)}`);

  for (let i = 0; i + 5 < edgePositions.length; i += 6) {
    const pa = toPlane(edgePositions[i]!, edgePositions[i + 1]!, edgePositions[i + 2]!);
    const pb = toPlane(edgePositions[i + 3]!, edgePositions[i + 4]!, edgePositions[i + 5]!);
    if (Math.abs(pa.dist) > planeTol || Math.abs(pb.dist) > planeTol) continue;
    const a = pa.uv;
    const b = pb.uv;
    if (hasTrueVerts) {
      // Straight edge = one segment between two true vertices → midpoint.
      if (isTrueVert(a) && isTrueVert(b)) {
        addPoint([(a[0] + b[0]) / 2, (a[1] + b[1]) / 2], 'midpoint');
      }
    } else {
      addPoint(a, 'endpoint');
      addPoint(b, 'endpoint');
      addPoint([(a[0] + b[0]) / 2, (a[1] + b[1]) / 2], 'midpoint');
    }
    segments.push({ a, b });
  }
  return { points, segments };
}

// ---------------------------------------------------------------------------
// 3D snapping (Move tool): world-space candidates from body display edges.
// Resolution is screen-space (the tool projects candidates through its
// camera), so core only collects.
// ---------------------------------------------------------------------------

export type V3 = [number, number, number];

export interface SnapPoint3 {
  readonly p: V3;
  readonly kind: 'endpoint' | 'midpoint';
}

export interface SnapSegment3 {
  readonly a: V3;
  readonly b: V3;
}

/**
 * Endpoints + midpoints (deduped) and segments from world edge pairs.
 * With `vertexPositions` (true B-rep vertices): only those are endpoints,
 * and midpoints appear only on straight edges (see collectPlanarSnaps).
 */
export function collectEdgeSnaps3D(
  edgePositions: Float32Array,
  vertexPositions?: Float32Array,
): {
  points: SnapPoint3[];
  segments: SnapSegment3[];
} {
  const points: SnapPoint3[] = [];
  const segments: SnapSegment3[] = [];
  const seen = new Set<string>();
  const keyOf = (p: V3): string =>
    `${Math.round(p[0] * 1e3)}:${Math.round(p[1] * 1e3)}:${Math.round(p[2] * 1e3)}`;
  const add = (p: V3, kind: 'endpoint' | 'midpoint'): void => {
    const key = `${kind}:${keyOf(p)}`;
    if (seen.has(key)) return;
    seen.add(key);
    points.push({ p, kind });
  };

  const hasTrueVerts = vertexPositions !== undefined && vertexPositions.length > 0;
  const trueVertKeys = new Set<string>();
  if (hasTrueVerts) {
    for (let i = 0; i + 2 < vertexPositions.length; i += 3) {
      const p: V3 = [vertexPositions[i]!, vertexPositions[i + 1]!, vertexPositions[i + 2]!];
      add(p, 'endpoint');
      trueVertKeys.add(keyOf(p));
    }
  }

  for (let i = 0; i + 5 < edgePositions.length; i += 6) {
    const a: V3 = [edgePositions[i]!, edgePositions[i + 1]!, edgePositions[i + 2]!];
    const b: V3 = [edgePositions[i + 3]!, edgePositions[i + 4]!, edgePositions[i + 5]!];
    if (hasTrueVerts) {
      if (trueVertKeys.has(keyOf(a)) && trueVertKeys.has(keyOf(b))) {
        add([(a[0] + b[0]) / 2, (a[1] + b[1]) / 2, (a[2] + b[2]) / 2], 'midpoint');
      }
    } else {
      add(a, 'endpoint');
      add(b, 'endpoint');
      add([(a[0] + b[0]) / 2, (a[1] + b[1]) / 2, (a[2] + b[2]) / 2], 'midpoint');
    }
    segments.push({ a, b });
  }
  return { points, segments };
}

/**
 * Resolve the strongest snap for `raw` within `tol` (sketch-plane units).
 * `segments` powers on-edge + extension; `dirSegments` (usually the sketch's
 * OWN entities — a deliberate, small set) powers parallel/perpendicular.
 * Feeding every model edge into `dirSegments` makes labels meaningless: with
 * enough reference directions some ray always passes near the cursor.
 */
export function resolveSnap(
  raw: Vec2,
  tol: number,
  points: readonly SnapPoint[],
  segments: readonly SnapSegment[],
  prev: Vec2 | null,
  axisAlign: boolean,
  grid: number,
  dirSegments: readonly SnapSegment[] = [],
): SnapResult {
  // 1. Point snaps — nearest candidate wins.
  let best: SnapPoint | null = null;
  let bestD = tol;
  for (const c of points) {
    const d = Math.hypot(raw[0] - c.p[0], raw[1] - c.p[1]);
    if (d < bestD) {
      best = c;
      bestD = d;
    }
  }
  if (best) return { p: best.p, kind: best.kind };

  // 2. On-edge — perpendicular foot on the nearest segment.
  let bestSeg: Vec2 | null = null;
  let bestSegD = tol;
  for (const s of segments) {
    const dx = s.b[0] - s.a[0];
    const dy = s.b[1] - s.a[1];
    const len2 = dx * dx + dy * dy;
    if (len2 < 1e-12) continue;
    let t = ((raw[0] - s.a[0]) * dx + (raw[1] - s.a[1]) * dy) / len2;
    t = Math.max(0, Math.min(1, t));
    const fx = s.a[0] + t * dx;
    const fy = s.a[1] + t * dy;
    const d = Math.hypot(raw[0] - fx, raw[1] - fy);
    if (d < bestSegD) {
      bestSeg = [fx, fy];
      bestSegD = d;
    }
  }
  if (bestSeg) return { p: bestSeg, kind: 'on-edge' };

  // 3. Directional inference — edge extensions, then parallel/perpendicular
  // to reference edges (drawing direction from `prev`). Nearest wins.
  let inferred: SnapResult | null = null;
  let inferredD = tol;

  // 3a. Edge extension: the infinite line beyond a segment's ends (limited
  // to a few lengths so distant edges don't grab the cursor).
  for (const s of segments) {
    const dx = s.b[0] - s.a[0];
    const dy = s.b[1] - s.a[1];
    const len2 = dx * dx + dy * dy;
    if (len2 < 1e-12) continue;
    const t = ((raw[0] - s.a[0]) * dx + (raw[1] - s.a[1]) * dy) / len2;
    if (t >= 0 && t <= 1) continue; // that's on-edge territory, already lost
    if (t < -3 || t > 4) continue;
    const fx = s.a[0] + t * dx;
    const fy = s.a[1] + t * dy;
    const d = Math.hypot(raw[0] - fx, raw[1] - fy);
    if (d < inferredD) {
      inferredD = d;
      inferred = {
        p: [fx, fy],
        kind: 'extension',
        ghost: { a: t < 0 ? s.a : s.b, b: [fx, fy] },
      };
    }
  }

  // 3b. Parallel / perpendicular to reference edge directions.
  if (axisAlign && prev) {
    const dirs: { e: Vec2; kind: 'parallel' | 'perpendicular' }[] = [];
    const seenAngles = new Set<number>();
    for (const s of dirSegments) {
      const dx = s.b[0] - s.a[0];
      const dy = s.b[1] - s.a[1];
      const len = Math.hypot(dx, dy);
      if (len < 1e-9) continue;
      // Quantize mod π so opposite directions dedupe (2° buckets).
      const key = Math.round((((Math.atan2(dy, dx) + Math.PI) % Math.PI) / Math.PI) * 90);
      if (seenAngles.has(key)) continue;
      seenAngles.add(key);
      dirs.push({ e: [dx / len, dy / len], kind: 'parallel' });
      dirs.push({ e: [-dy / len, dx / len], kind: 'perpendicular' });
    }
    const ux = raw[0] - prev[0];
    const uy = raw[1] - prev[1];
    for (const { e, kind } of dirs) {
      const proj = ux * e[0] + uy * e[1];
      if (Math.abs(proj) < tol * 1.5) continue; // too close to prev to infer
      const fx = prev[0] + e[0] * proj;
      const fy = prev[1] + e[1] * proj;
      const d = Math.hypot(raw[0] - fx, raw[1] - fy);
      if (d < inferredD) {
        inferredD = d;
        inferred = { p: [fx, fy], kind, ghost: { a: prev, b: [fx, fy] } };
      }
    }
  }
  const [x, y] = raw;

  // 3c. Axis DIRECTION lock: the segment from `prev` runs parallel to the
  // sketch X (red) or Y (green) axis — SketchUp's core inference. Competes by
  // perpendicular offset so it fights fairly with parallel/extension above.
  if (axisAlign && prev) {
    const dY = Math.abs(x - prev[0]); // vertical run → parallel to Y axis
    if (dY < inferredD) {
      inferredD = dY;
      inferred = { p: [prev[0], y], kind: 'axis-y', ghost: { a: prev, b: [prev[0], y] } };
    }
    const dX = Math.abs(y - prev[1]); // horizontal run → parallel to X axis
    if (dX < inferredD) {
      inferredD = dX;
      inferred = { p: [x, prev[1]], kind: 'axis-x', ghost: { a: prev, b: [x, prev[1]] } };
    }
  }

  // 3d. On-axis lock through the sketch ORIGIN — lets even the FIRST point land
  // exactly on the red/green axis (SketchUp "On Red/Green Axis").
  if (axisAlign) {
    if (Math.abs(y) < inferredD) {
      inferredD = Math.abs(y);
      inferred = { p: [x, 0], kind: 'axis-x', ghost: { a: [0, 0], b: [x, 0] } };
    }
    if (Math.abs(x) < inferredD) {
      inferredD = Math.abs(x);
      inferred = { p: [0, y], kind: 'axis-y', ghost: { a: [0, 0], b: [0, y] } };
    }
  }
  if (inferred) return inferred;

  // 4. Grid.
  return { p: [Math.round(x / grid) * grid, Math.round(y / grid) * grid], kind: 'grid' };
}
